import { useState, useMemo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import awsExports from './aws-exports';
import { Amplify, I18n, Auth } from 'aws-amplify';
import { Authenticator,AmplifyProvider,translations } from '@aws-amplify/ui-react';
import { UserContext } from './context/UserContext';
import { useUser } from './hooks/useUser';
import { useNotification } from './hooks/useNotification';


import {Navigation} from './pages/Navigation';
import {Page404} from './pages/Page-404';
import {Home} from './pages/Home';
import {PlanIndex} from './pages/plans/index';
import {PlanCertificate} from './pages/plans/certificate';
import {MapsIndex} from './pages/maps/index';
import {ViewerMapOverview} from "./xPlanViewer/viewerMapOverview";
import {OrdersIndex} from './pages/orders/index';
import {Order} from './pages/order/index';
import {OrdersTenderIndex} from './pages/ordersTender/index';
import {OrdersManagementIndex} from './pages/ordersManagement/index';

import {XPlanViewer} from './xPlanViewer/XPlanViewer';
import {HelpPage} from './pages/help/index';

import {CompanyPage} from './pages/company/index';
import {CompanyUsers} from './pages/company/users';
import {CompanyProducts} from './pages/company/license';
import {CompanyProductsDetails} from './pages/company/licenseDetails';
import {CompanyIntegrations} from './pages/company/integrations';
import {Intergrations} from './pages/company/integrations/index';

import {XplanStylerPage} from './pages/company/xplanStyler';
import {UserPage} from './pages/user';
import {Notifications} from './pages/notifications/index';
import { Signup } from './pages/Signup'
import { ExternalAccess } from './pages/ExternalAccess';

import { formFields } from './components/authentication/formFields';
import { components } from './components/authentication/components';

import '@aws-amplify/ui-react/styles.css';
import '@fontsource/inter';
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import './App.scss';


Amplify.configure(awsExports);
Auth.configure(awsExports);
I18n.putVocabularies(translations);
I18n.setLanguage('de');

const App = () => {
	const { user, company } = useUser();
	const { unreadNotifications, updateNotificationCount } = useNotification(user?.id);
	const queryParameters = new URLSearchParams(window.location.search);
	const file = queryParameters.get('file');
	const [navigationOpen, setNavigationOpen] = useState(localStorage.getItem('NavigationOpen'))
	const nonAuthRoutes = useMemo(() => (['integration', 'open-data', 'signup', 'signin', 'share', 'linkshare']), []);

	if( nonAuthRoutes.includes(window.location.pathname.split("/")[1]?.split('?')[0]) ) {
		return (
			<AmplifyProvider >
				<BrowserRouter>
					<Routes>
						<Route path='/integration/maps/:integrationAccessId' element={<ViewerMapOverview/>} />
						<Route path='/integration/maps/:integrationAccessId/:integrationPlanId' element={<XPlanViewer/>} />
						<Route path='/open-data/plan/:openDataPlanId' element={<XPlanViewer/>} />
						<Route path='/signup' element={<Signup signUp={true}/>} />
						<Route path='/signin' element={<Signup signUp={false}/>} />
						<Route path='/share/:accessId' element={<ExternalAccess/>} />
						<Route path='/linkshare/:linkSharedAccessId' element={<XPlanViewer/>} />
					</Routes>
				</BrowserRouter>
			</AmplifyProvider>
		);
	} else {
		return (
			<AmplifyProvider >
				<Authenticator formFields={formFields}  components={components}>
					<UserContext.Provider className={"wrapper"} value={{user, company, navigationOpen, setNavigationOpen, unreadNotifications, updateNotificationCount}}>
						<BrowserRouter>
							{(user && (
								<main className="app bg-gray-100 h-dvh">
									{(file === null) && (<Navigation/>)}
									<div className={`${navigationOpen === 0 ? " lg:pl-[240px] ":" lg:pl-[80px] "} max-h-[calc(100vh-64px)] lg:max-h-dvh overflow-y-auto`}>
										<Routes>
											{/* Order Route */}

											<Route path='/order/:id' element={<Order/>} ></Route>

											{/* ----- Creator-Only Routes ----- */}
											<Route path='/ordersTender/' element={<OrdersTenderIndex/>} ></Route>
											<Route path='/ordersManagement/' element={<OrdersManagementIndex/>} ></Route>

											{/* Orders Routes */}
											<Route path='/orders' element={<OrdersIndex data={"myPlans"}/>} ></Route>
											<Route path='/orders/:type' element={<OrdersIndex data={"myPlans"}/>} ></Route>

											{/* XPlan Viewer Routes */}
											<Route path='/viewer/:mapsId/:id' element={<XPlanViewer/>} ></Route>
											<Route path='/viewer/:id' element={<XPlanViewer/>} />
											<Route path='/viewer' element={<XPlanViewer/>} />

											{/* Plan Routes */}
											<Route path='/plans' element={<PlanIndex data={"myPlans"} />} ></Route>
											<Route path='/plan/:id/certificate' element={<PlanCertificate/>} ></Route>

											{/* Map Routes */}
											<Route path='/maps/:mapId' element={<ViewerMapOverview  />}  ></Route>
											<Route path='/maps' element={<MapsIndex  data={"myMaps"} />}  ></Route>

											{/* Company Routes */}
											<Route path="/company" element={<CompanyPage />}></Route>
											<Route path="/company/license" element={<CompanyProducts/>}></Route>
											<Route path="/company/integrations" element={<CompanyIntegrations/>}></Route>
											<Route path="/company/integrations/:id" element={<Intergrations/>}></Route>
											<Route path="/company/users" element={<CompanyUsers/>}></Route>
											<Route path="/company/xplanStyler" element={<XplanStylerPage/>}></Route>

											<Route path="/products/:feature" element={<CompanyProductsDetails/>}></Route>



											{/* Personal Routes */}
											<Route path="/user" element={<UserPage />}></Route>
											<Route path="/notifications" element={<Notifications data={"myNotification"}/>}></Route>

											{/* Other Routes */}
											<Route path="/help" element={<HelpPage />}></Route>
											<Route path='/' element={<Home/>} ></Route>
											<Route path='*' element={<Page404/>} ></Route>
										</Routes>
									</div>
								</main>
							))}
						</BrowserRouter>
					</UserContext.Provider>
				</Authenticator>
			</AmplifyProvider>
		);
	}
}

export default App;
